import React, { useEffect, useMemo } from 'react';

const IMAGE_NAME = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

function selectedFiles(value, multiple) {
  if (multiple) return Array.isArray(value) ? value.filter(Boolean) : [];
  return value instanceof File ? [value] : [];
}

function isImageFile(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  return IMAGE_NAME.test(file.name || '');
}

function fileKey(file) {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

export default function FileUploadField({
  id,
  name,
  value,
  onChange,
  multiple = false,
  required = false,
  disabled = false,
  accept,
}) {
  const files = selectedFiles(value, multiple);
  const previews = useMemo(() => selectedFiles(value, multiple).map((file) => {
      const image = isImageFile(file);
      return {
        key: fileKey(file),
        name: file.name,
        isImage: image,
        url: image ? URL.createObjectURL(file) : '',
      };
    }), [value, multiple]);

  useEffect(() => {
    return () => {
      previews.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
    };
  }, [previews]);

  return (
    <div className="review-file-field">
      <input
        id={id}
        name={name}
        className="review-control"
        type="file"
        accept={accept}
        multiple={multiple}
        required={Boolean(required) && files.length === 0}
        disabled={disabled}
        onChange={(event) => {
          const next = Array.from(event.target.files || []);
          if (!multiple) {
            onChange(next[0] || '');
            return;
          }

          const currentFiles = selectedFiles(value, true);
          const existingKeys = new Set(
            currentFiles.map((file) => `${file.name}-${file.size}-${file.lastModified}`),
          );
          const newFiles = next.filter(
            (file) => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`),
          );
          onChange([...currentFiles, ...newFiles]);
          event.target.value = '';
        }}
      />
      {previews.length ? (
        <div className="review-file-preview">
          <div className="review-file-preview__images">
            {previews.map((item, index) => (
              <div key={item.key} className="review-file-preview__item">
                {item.isImage ? (
                  <img
                    className="review-file-preview__thumb"
                    src={item.url}
                    alt={item.name}
                    title={item.name}
                  />
                ) : (
                  <span className="review-file-preview__name">{item.name}</span>
                )}
                <button
                  type="button"
                  className="review-file-preview__remove"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => {
                    if (multiple) {
                      onChange(files.filter((_, fileIndex) => fileIndex !== index));
                    } else {
                      onChange('');
                    }
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
